// ChoiceDialog — a modal that offers a small set of MUTUALLY-EXCLUSIVE actions,
// each stated in plain language (Agreement Command Center design language: a
// choice is a statement of what each option will do). The sibling of
// ConsequenceConfirm for the case where there is more than one way forward.
//
// Composed on the DS `.modal` primitive exactly like ConsequenceConfirm (portal
// to document.body, focus trap, Escape + scrim = cancel, inert background). Focus
// lands on Cancel so a stray Enter never triggers a consequential action.

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';

export interface ChoiceOption {
  /** Stable key for the option (React list key). */
  key: string;
  /** The button label. */
  label: string;
  /** Plain-language statement of what choosing this does. */
  description: ReactNode;
  tone?: 'danger' | 'default';
  onSelect: () => void;
}

export interface ChoiceDialogProps {
  open: boolean;
  title: string;
  /** Framing shown above the options — what the overall decision is. */
  intro?: ReactNode;
  options: ChoiceOption[];
  busy?: boolean;
  onCancel: () => void;
}

export function ChoiceDialog({
  open,
  title,
  intro,
  options,
  busy = false,
  onCancel,
}: ChoiceDialogProps): React.JSX.Element | null {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const containerRef = useFocusTrap(open);

  const handleEscape = useCallback(() => {
    if (!busy) onCancel();
  }, [busy, onCancel]);
  useEscapeKey(handleEscape, open);
  useInertBackground(open);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const titleId = 'choice-dialog-title';
  const descId = 'choice-dialog-desc';

  const dialog = (
    <>
      <div
        className="modal-backdrop"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />
      <div
        ref={containerRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="modal"
        data-size="sm"
      >
        <div className="modal-header">
          <h2 id={titleId} className="modal-title">
            {title}
          </h2>
        </div>
        <div className="modal-body">
          {intro != null && (
            <p id={descId} className="modal-description">
              {intro}
            </p>
          )}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
          >
            {options.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className="btn"
                data-variant={opt.tone === 'danger' ? 'danger' : 'secondary'}
                onClick={opt.onSelect}
                disabled={busy}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 4,
                  height: 'auto',
                  textAlign: 'left',
                  padding: 'var(--space-3) var(--space-4)',
                  whiteSpace: 'normal',
                }}
              >
                <span style={{ fontWeight: 'var(--weight-medium)' }}>{opt.label}</span>
                <span
                  className="oc-meta"
                  style={{ fontWeight: 'normal', lineHeight: 1.4 }}
                >
                  {opt.description}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button
            ref={cancelRef}
            type="button"
            className="btn"
            data-variant="ghost"
            onClick={onCancel}
            disabled={busy}
          >
            {busy ? 'Working…' : 'Cancel'}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}
